/**
 * Everything the HUD shows, derived rather than drawn.
 *
 * The same split as `progress.ts`, for the same reason: the decisions — how
 * much time is left on a ticket, how alarmed it should look, what the round
 * clock reads — are pure functions of a snapshot and a tick, so they can be
 * asserted without a browser. `hud.tsx` below this file is layout and refs.
 *
 * The split also serves the React/canvas rule. The
 * *discrete* half (which tickets exist, what the score is) is what React
 * reconciles, and `hudSignature` is how it knows something discrete changed.
 * The *continuous* half (fuses, the round clock) is recomputed every animation
 * frame and written straight to the DOM through refs, so a burning fuse costs
 * no renders at all.
 *
 * Nothing here reads `timings.json`. A ticket carries both its own stamps and
 * the round carries its own duration, so a stored log renders its own HUD
 * correctly even after the numbers have been retuned.
 */

import type { DishRequirement, DishSummary, Order } from '@overcooked/shared'
import { TICK_MS } from '@overcooked/shared'
import type { FeedSnapshot, OrderCue, RefusalNote } from '../feed/feed'
import { COLOURS } from './theme'

/** How alarmed a ticket looks. Four coarse steps, because React renders on them. */
export type Urgency = 'calm' | 'warm' | 'urgent' | 'gone'

export interface Ticket {
  id: string
  dishId: string
  /** From the server's dish catalogue; falls back to the raw id. */
  label: string
  contents: string[]
  /**
   * The recipe, as the ticket has to show it: which crate ingredients go in,
   * which vessel they need and which equipment they pass through. Derived on
   * the server from `recipes.json` — phase 8's single most important change,
   * because without it a player has no way to learn that a vegetable stew is
   * three vegetables stewed one at a time onto one plate.
   */
  requires: DishRequirement
  /**
   * Extra points a matched serve of this dish pays (`recipes.json`
   * `dishes[].bonus`). 0 when the dish has none, or the catalogue predates
   * bonuses.
   */
  bonus: number
  spawnedAtTick: number
  expiresAtTick: number
}

/**
 * The chip a recipe card wears when its dish pays a bonus: `"+5"` for a bonus
 * of 5, `null` (no chip) for none. One function so the docket, the recipe
 * panel and the landing menu can never disagree about what a dish is worth.
 */
export function bonusChip(bonus: number | undefined): { text: string; title: string } | null {
  if (!bonus || bonus <= 0) return null
  return {
    text: `+${bonus}`,
    title: `Harder dish: worth ${bonus} extra point${bonus === 1 ? '' : 's'}`,
  }
}

/** An empty recipe, for a dish id the server did not describe. */
const NO_REQUIREMENT: DishRequirement = { items: [], containers: [], stations: [] }

/**
 * A seat, for the roster in the round rail.
 *
 * Discrete by construction — an id, a seat number and whether the socket is up.
 * Nothing continuous, so it may go through React with the rest of the model.
 */
export interface SeatRow {
  id: string
  seat: number
  connected: boolean
}

export interface HudModel {
  tickets: Ticket[]
  seats: SeatRow[]
  score: number
  roundDurationTicks: number
  orderCues: OrderCue[]
  /**
   * The newest thing this player was refused, if it is still fresh. Discrete —
   * it changes when a press is refused and when the sentence times out, not
   * every frame — so it may travel with the rest of the reconciled model.
   */
  refusal: RefusalNote | null
  /** Cheap equality for the discrete half: re-render only when this changes. */
  signature: string
}

interface Fuse {
  /** 1 when the ticket has just appeared, 0 when it is out of time. */
  fraction: number
  remainingTicks: number
  remainingMs: number
  urgency: Urgency
  colour: string
  /**
   * How hard the card should be moving, 0..1. Colour is never the only channel:
   * the CSS uses this to tilt and pulse the whole docket, so a ticket about to
   * lapse is visible from the corner of the eye and in greyscale.
   */
  agitation: number
}

interface RoundClock {
  remainingTicks: number
  remainingMs: number
  /** 1 at the start of the round, 0 at the end. */
  fraction: number
  label: string
  urgency: Urgency
  /** Whether this clock is showing a round that is over. `label === '0:00'`. */
  over: boolean
}

// A ticket's fuse and the round clock share one ramp, so "nearly out of time"
// looks the same wherever it appears.
const CALM_COLOUR = COLOURS.mint
const WARM_COLOUR = COLOURS.brass
const URGENT_COLOUR = COLOURS.blood

/** Below this fraction remaining a ticket is warm; below the second, urgent. */
export const WARM_BELOW = 0.5
export const URGENT_BELOW = 0.25

export function urgencyOf(fraction: number): Urgency {
  if (fraction <= 0) return 'gone'
  if (fraction < URGENT_BELOW) return 'urgent'
  if (fraction < WARM_BELOW) return 'warm'
  return 'calm'
}

export function urgencyColour(urgency: Urgency): string {
  if (urgency === 'calm') return CALM_COLOUR
  if (urgency === 'warm') return WARM_COLOUR
  return URGENT_COLOUR
}

/**
 * A ticket's remaining life, measured against its own window.
 *
 * The denominator is `expiresAtTick - spawnedAtTick` rather than anything
 * recomputed from `timings.json`, which is what lets a log recorded under one
 * set of timings render honestly under another — and it matters more now that
 * two tickets in one round can have different-length fuses, because the budget
 * is the dish's cook time plus a slack that decays by slot.
 */
export function ticketFuse(ticket: Pick<Order, 'spawnedAtTick' | 'expiresAtTick'>, atTick: number): Fuse {
  const total = ticket.expiresAtTick - ticket.spawnedAtTick
  const remainingTicks = Math.max(0, ticket.expiresAtTick - atTick)
  const fraction = total <= 0 ? 0 : Math.min(1, remainingTicks / total)
  const urgency = urgencyOf(fraction)
  return {
    fraction,
    remainingTicks,
    remainingMs: remainingTicks * TICK_MS,
    urgency,
    colour: urgencyColour(urgency),
    // Nothing at all until the ticket is warm, then squared, so the last
    // quarter of the window is unmistakably different from the first half.
    agitation:
      fraction >= WARM_BELOW ? 0 : Math.min(1, ((WARM_BELOW - fraction) / WARM_BELOW) ** 2),
  }
}

/** `2:31`, `0:07`. Never negative, never a bare number of seconds. */
export function formatClock(ms: number): string {
  const whole = Math.max(0, Math.ceil(ms / 1000))
  const minutes = Math.floor(whole / 60)
  const seconds = whole % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * The round clock, and the one rule it has to keep: 0:00 means over.
 *
 * `running` is what enforces it. The tick is an estimate — a smooth local
 * clock corrected toward the server (see `render/tickClock.ts`) — so it reaches
 * the round's last tick a fraction of a second before the round's end is known,
 * and on the old unanchored clock it got there a great deal earlier than that.
 * Either way the player was shown 0:00 in a kitchen still taking their keys,
 * which is the "the clock reached zero and the round kept going" report.
 *
 * So while the round is still running the clock is floored at one tick and
 * reads 0:01, however far past the end the estimate has drifted. The last
 * second is held rather than a lie told, and the moment the round is actually
 * over — `feed.ended`, from the whistle or from the clock latch behind it — the
 * caller passes `running: false` and the same function draws the zero.
 */
export function roundClock(atTick: number, durationTicks: number, running = false): RoundClock {
  // Clamped at the round's own length in both directions: an estimate that ran
  // past the end would otherwise draw a negative clock. The server stops the
  // loop at exactly `durationTicks`, so 0:00 is the floor and there is nothing
  // beyond it — and while the round is running the floor is one tick above it.
  const remainingTicks = Math.max(running ? 1 : 0, durationTicks - Math.floor(atTick))
  const fraction = durationTicks <= 0 ? 0 : Math.min(1, remainingTicks / durationTicks)
  const remainingMs = remainingTicks * TICK_MS
  return {
    remainingTicks,
    remainingMs,
    fraction,
    label: formatClock(remainingMs),
    urgency: urgencyOf(fraction),
    over: remainingTicks === 0,
  }
}

/**
 * Where a tick sits in a recording's audio, in seconds.
 *
 * The offset needs no new data: an 880 Hz tone is burned into every recording
 * at the tick the trace saw it, so `syncBeepTick` is the one number that ties
 * the tape's clock to the microphone's. A version-1 log, or one where no beep
 * was heard, has no anchor — treating that as tick 0 is the honest fallback,
 * because it is what the recorder tries to make true.
 *
 * It lives beside `roundClock` for the same reason `roundClock` lives here: it
 * is the checkable half of a thing whose other half is a browser API.
 */
export function tickToSeconds(tick: number, syncBeepTick: number | null): number {
  return Math.max(0, (tick - (syncBeepTick ?? 0)) * TICK_MS) / 1000
}

/**
 * The discrete half of the HUD.
 *
 * Tickets are sorted closest-to-expiry first, which is the order the engine
 * fills them in — so the leftmost ticket is always the one a
 * serve of that dish would satisfy, and the queue reads as a queue rather than
 * as an arrival log.
 */
export function deriveHud(snapshot: FeedSnapshot, dishes: DishSummary[] = []): HudModel {
  const state = snapshot.state
  const byId = new Map(dishes.map((d) => [d.id, d]))

  const tickets: Ticket[] = (state?.orders ?? [])
    .map((order) => {
      const dish = byId.get(order.dishId)
      return {
        id: order.id,
        dishId: order.dishId,
        label: dish?.label ?? order.dishId,
        contents: dish ? [...dish.contents] : [],
        requires: dish?.requires ?? NO_REQUIREMENT,
        bonus: dish?.bonus ?? 0,
        spawnedAtTick: order.spawnedAtTick,
        expiresAtTick: order.expiresAtTick,
      }
    })
    .sort((a, b) => a.expiresAtTick - b.expiresAtTick || (a.id < b.id ? -1 : 1))

  const score = state?.score ?? 0
  const roundDurationTicks = state?.roundDurationTicks ?? 0
  const seats: SeatRow[] = (state?.players ?? [])
    .map((p) => ({ id: p.id, seat: p.seat, connected: p.connected }))
    .sort((a, b) => a.seat - b.seat)

  return {
    tickets,
    seats,
    score,
    roundDurationTicks,
    orderCues: snapshot.orderCues,
    refusal: snapshot.refusal,
    signature: hudSignature(tickets, score, snapshot.orderCues, seats, snapshot.refusal),
  }
}

/**
 * What React needs to re-render for. Deliberately excludes every continuous
 * value: a fuse burning down must not cost a reconciliation, only a ticket
 * appearing or leaving should.
 */
export function hudSignature(
  tickets: Ticket[],
  score: number,
  orderCues: OrderCue[],
  seats: SeatRow[] = [],
  refusal: RefusalNote | null = null,
): string {
  return [
    score,
    tickets.map((t) => t.id).join(','),
    // The leading docket is the only one that draws a route strip, and the
    // strip is a fold over the *dish*, not over the order. Named here so the
    // thing the strip actually depends on is in the gate explicitly rather than
    // riding on the ticket ids happening to move with it — a later change to
    // the sort must not silently strand the strip on a stale dish.
    tickets[0]?.dishId ?? '',
    orderCues.map((c) => `${c.kind}:${c.orderId}`).join(','),
    seats.map((s) => `${s.seat}${s.connected ? '+' : '-'}`).join(','),
    // The stamp, not just the reason: pressing into the same refusal twice is
    // two separate answers and the flash line should say so twice.
    refusal ? `${refusal.reason}@${refusal.shownAt}` : '',
  ].join('|')
}
